import {
  toWSDate,
  WSAvatar,
  WSElement,
  WSGrid,
  WSList,
  WSLoader,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSPill,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IRequirementDefinitionResponse,
  RequirementDefinitionStatus,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { SharedFileRequestJobType } from "@wingspanhq/shared-files/dist/lib/interfaces";
import React, { useEffect, useState } from "react";
import { useRouteMatch } from "react-router-dom";

import { WSQueries } from "../../../../query/WSQuery";
import { useQueryBackgroundChecksSettings } from "../../../../query/backgroundChecks/queries/useQueryBackgroundChecksSettings";
import { useQueryEngagementsListAll } from "../../../../query/engagements/queries/useQueryEngagementsListAll";
import { useQueryMonitorSettings } from "../../../../query/monitorSettings/query/useQueryMonitorSettings";
import { useQueryRequirementDefinition } from "../../../../query/requirementDefinitions/query/useQueryRequirementDefinition";
import { useQuerySharedFileSettings } from "../../../../query/sharedFile/query/useQuerySharedFileSettings";
import { useQuerySignedDocumentTemplate } from "../../../../query/signedDocuments/query/useQuerySignedDocumentTemplate";
import { WayfinderWidget } from "../../../../shared/components/WayfinderWidget";
import { AIValidationWidget } from "../../../Requirements/components/AIValidationWidget";
import { ReportsInfoWidgetContent } from "../../../Requirements/modules/BackgroundChecks/components/ReportsInfoWidget";
import { selectorIsSharedFileNotNoneContext } from "../../../Requirements/selectors/selectorIsSharedFileNotNoneContext";
import { RequirementContextType } from "../../../Requirements/types";
import { ApprovalWidget } from "../../components/ApprovalWidget";
import { RequirementInformation } from "../../components/RequirementInformation";
import { VersionsWidget } from "../../components/VersionsWidget";
import { useRequirementDefinitionDefaultActions } from "../../hooks/useRequirementDefinitionDefaultActions";
import { IDENTITY_VERIFICATION_CHECKS } from "../../modules/IdentityVerification/constants";
import { useGoToRequirementDefinitions } from "../../paths/helpers";

import { EngagementsTable } from "./EngagementsTable";
import { SelectRequirementDefinitionVersion } from "./SelectRequirementDefinitionVersion";
import { RequirementDefinitionHistoryWidget } from "./useRequirementDefinitionHistoryWidget";

export const RouteRequirementDetails: React.FC = () => {
  const match = useRouteMatch<{ requirementId: string }>();
  const queryRequirementDefinition = useQueryRequirementDefinition(
    match.params.requirementId
  );

  return (
    <WSPage>
      <WSQueries queries={{ queryRequirementDefinition }}>
        {({ queryRequirementDefinitionData }) => (
          <RequirementDetails
            requirementDefinition={queryRequirementDefinitionData}
          />
        )}
      </WSQueries>
    </WSPage>
  );
};

const RequirementDetails: React.FC<{
  requirementDefinition: IRequirementDefinitionResponse;
}> = props => {
  const [requirementDefinition, setRequirementDefinition] =
    useState<IRequirementDefinitionResponse>(props.requirementDefinition);

  useEffect(() => {
    setRequirementDefinition(props.requirementDefinition);
  }, [props.requirementDefinition]);

  const requirementId = requirementDefinition.requirementDefinitionId;
  const goToRequirementDefinitions = useGoToRequirementDefinitions();
  const requirementDefinitionDefaultActions =
    useRequirementDefinitionDefaultActions();
  const queryEngagementsList = useQueryEngagementsListAll();

  const signedDocumentTemplateQuery = useQuerySignedDocumentTemplate(
    requirementDefinition.dataSourceId as string,
    {
      enabled:
        requirementDefinition.requirementType === RequirementType.Signature &&
        !!requirementDefinition.dataSourceId
    }
  );
  const sharedFileSettingsQuery = useQuerySharedFileSettings(
    requirementDefinition.dataSourceId as string,
    {
      enabled:
        requirementDefinition.requirementType === RequirementType.SharedFile &&
        !!requirementDefinition.dataSourceId
    }
  );

  const backgroundChecksSettingsQuery = useQueryBackgroundChecksSettings(
    requirementDefinition.dataSourceId as string,
    {
      enabled:
        requirementDefinition.requirementType ===
          RequirementType.BackgroundCheck &&
        !!requirementDefinition.dataSourceId
    }
  );

  const monitorSettingsQuery = useQueryMonitorSettings(
    requirementDefinition.dataSourceId as string,
    {
      enabled:
        requirementDefinition.requirementType ===
          RequirementType.InsuranceCoverage &&
        !!requirementDefinition.dataSourceId
    }
  );

  return (
    <WSQueries queries={{ queryEngagementsList }}>
      {({ queryEngagementsListData }) => {
        const loadingByType: Record<RequirementType, boolean> = {
          [RequirementType.Signature]:
            signedDocumentTemplateQuery.isInitialLoading,
          [RequirementType.SharedFile]:
            sharedFileSettingsQuery.isInitialLoading,
          [RequirementType.BackgroundCheck]:
            backgroundChecksSettingsQuery.isInitialLoading,
          [RequirementType.IdentityVerification]: false,
          [RequirementType.InsuranceCoverage]:
            monitorSettingsQuery.isInitialLoading,
          [RequirementType.Registration]: false,
          [RequirementType.TaxVerification]: false,
          [RequirementType.PayoutMethod]: false
        };

        if (loadingByType[requirementDefinition.requirementType]) {
          return <WSLoader.Spinner mt="3XL" size="M" />;
        }

        const validationJobs =
          sharedFileSettingsQuery.data?.jobs?.filter(
            job => job.type === SharedFileRequestJobType.Validation
          ) || [];

        const aiSupportByType: Record<RequirementType, boolean> = {
          [RequirementType.Signature]: false,
          [RequirementType.SharedFile]: selectorIsSharedFileNotNoneContext(
            sharedFileSettingsQuery?.data?.contextType
          ),
          [RequirementType.BackgroundCheck]: false,
          [RequirementType.IdentityVerification]: true,
          [RequirementType.InsuranceCoverage]: true,
          [RequirementType.Registration]: false,
          [RequirementType.TaxVerification]: false,
          [RequirementType.PayoutMethod]: false
        };

        const widgetsByType: Record<RequirementType, React.ReactNode> = {
          [RequirementType.Signature]: null,
          [RequirementType.SharedFile]: selectorIsSharedFileNotNoneContext(
            sharedFileSettingsQuery?.data?.contextType
          ) ? (
            <>
              <AIValidationWidget
                contextType={RequirementContextType.Payer}
                validationItems={validationJobs}
              />
              <WayfinderWidget />
            </>
          ) : null,
          [RequirementType.BackgroundCheck]: backgroundChecksSettingsQuery.data
            ?.package?.searches ? (
            <ReportsInfoWidgetContent
              searches={backgroundChecksSettingsQuery.data?.package?.searches}
            />
          ) : null,
          [RequirementType.IdentityVerification]: (
            <>
              <AIValidationWidget
                contextType={RequirementContextType.Payer}
                validationItems={IDENTITY_VERIFICATION_CHECKS}
              />
              <WayfinderWidget />
            </>
          ),
          [RequirementType.InsuranceCoverage]: (
            <>
              {/*
                // TODO: change to specific validations
                <AIValidationWidget
                  contextType={RequirementContextType.Payer}
                  validationItems={IDENTITY_VERIFICATION_CHECKS}
                />
                */}
              <WayfinderWidget />
            </>
          ),
          [RequirementType.Registration]: null,
          [RequirementType.TaxVerification]: null,
          [RequirementType.PayoutMethod]: null
        };

        const isDefaultRequirementActive =
          props.requirementDefinition.version === requirementDefinition.version;

        return (
          <WSElement>
            <WSPageToolbar
              actions={
                requirementDefinitionDefaultActions.getOnPreview(
                  requirementDefinition
                )
                  ? [
                      {
                        buttonKind: "Secondary",
                        icon: "view",
                        label: "Preview requirement",
                        onClick:
                          requirementDefinitionDefaultActions.getOnPreview(
                            requirementDefinition
                          )
                      }
                    ]
                  : undefined
              }
              breadcrumbs={[
                {
                  label: "Requirements",
                  onClick: () => {
                    goToRequirementDefinitions(requirementDefinition.status);
                  }
                },
                {
                  label: requirementDefinition.name
                }
              ]}
              fetchActions={async () =>
                requirementDefinitionDefaultActions
                  .getMenuActions({
                    engagements: queryEngagementsListData,
                    onDelete: requirementDefinition => {
                      goToRequirementDefinitions(requirementDefinition.status);
                    },
                    requirementDefinition: requirementDefinition
                  })
                  .then(actions =>
                    actions.filter(action => action.name !== "preview")
                  )
              }
              title={requirementDefinition.name}
              titleAfter={
                <>
                  <SelectRequirementDefinitionVersion
                    requirementDefinition={props.requirementDefinition}
                    value={requirementDefinition.version}
                    onChange={version => {
                      if (version === props.requirementDefinition.version) {
                        setRequirementDefinition(props.requirementDefinition);
                      } else {
                        const historyItem =
                          props.requirementDefinition.versionHistory.find(
                            v => v.version === version
                          );

                        if (historyItem) {
                          setRequirementDefinition({
                            ...props.requirementDefinition,
                            ...historyItem,
                            versionNote: historyItem.versionNote
                          });
                        }
                      }
                    }}
                  />
                  {requirementDefinition.status ===
                  RequirementDefinitionStatus.Inactive ? (
                    <WSPill icon="archive" text="Archived" theme="neutral" />
                  ) : props.requirementDefinition.versionHistory.length > 0 ? (
                    isDefaultRequirementActive ? (
                      <WSPill icon="check" text="Latest" theme="success" />
                    ) : null
                  ) : (
                    <WSPill icon="check" text="Ready" theme="success" />
                  )}

                  {aiSupportByType[requirementDefinition.requirementType] ? (
                    <WSAvatar
                      colorBackground="garnet"
                      format="square"
                      icon="compass"
                      size="L"
                      type="icon"
                    />
                  ) : null}
                  <WSText.ParagraphXs color="gray500">
                    Last modified{" "}
                    {toWSDate(requirementDefinition.updatedAt).formattedValue}
                  </WSText.ParagraphXs>
                </>
              }
            />

            <WSGrid>
              <WSGrid.Item span={{ l: "8", s: "12" }}>
                <WSList gap="XL">
                  <WSElement>
                    <WSSectionToolbar title="Requirement information" />
                    <WSPanel mt="M">
                      <RequirementInformation
                        backgroundChecksSettings={
                          backgroundChecksSettingsQuery?.data
                        }
                        monitorSettings={monitorSettingsQuery?.data}
                        requirementDefinition={requirementDefinition}
                        sharedFileSettings={sharedFileSettingsQuery?.data}
                        singedDocumentTemplate={
                          signedDocumentTemplateQuery?.data
                        }
                      />
                    </WSPanel>
                  </WSElement>

                  {isDefaultRequirementActive && (
                    <WSElement>
                      <WSSectionToolbar
                        mb="M"
                        title="Requirement in engagements"
                      />
                      <EngagementsTable requirementId={requirementId} />
                    </WSElement>
                  )}
                </WSList>
              </WSGrid.Item>
              <WSGrid.Item span={{ l: "4", s: "12" }}>
                <WSList gap="L">
                  <VersionsWidget
                    requirementDefinition={props.requirementDefinition}
                    viewingVersion={requirementDefinition.version}
                  />

                  <ApprovalWidget
                    reviewStrategy={requirementDefinition.reviewStrategy}
                  />

                  {widgetsByType[requirementDefinition.requirementType]}

                  {isDefaultRequirementActive && (
                    <RequirementDefinitionHistoryWidget
                      requirementDefinitionId={requirementId}
                    />
                  )}
                </WSList>
              </WSGrid.Item>
            </WSGrid>
          </WSElement>
        );
      }}
    </WSQueries>
  );
};
